import { PermissionRole } from '@next-space/fe-api-idl';
import { useCallback, useMemo } from 'react';
import { Avatar } from 'src/common/components/avatar';
import { Icon } from 'src/common/components/icon';
import type { ListItem } from 'src/common/components/list-view/types';
import { ListItemType } from 'src/common/components/list-view/types';
import { useCloseModal, useOpenModal } from 'src/common/components/next-modal';
import { getUrlWithoutDomain } from 'src/common/utils/url-utils';
import { useGetSharePageUrl } from 'src/components/share/helper';
import { usePermissions } from 'src/hooks/share/use-permissions';
import { useCurrentUser, useLogout } from 'src/hooks/user';
import { Modals } from 'src/modals';
import { SearchParams, ViewPath } from 'src/utils';
import { bizTracker } from 'src/utils/biz-tracker';
import { isPageLike } from 'src/utils/block-type-utils';
import { writeTextInClipboard } from 'src/utils/clipboard';
import { judgeSharePage, useGetPageId } from 'src/utils/getPageId';
import { splicePathSearchParams } from 'src/utils/history-utils';
import { locationToUrl } from 'src/utils/location-utils';
import { usePickBlock } from 'src/utils/pick-block';
import { useGetQuery } from '../page/use-get-query';
import { useIsNeedPay, usePageMeta } from '../page/use-page-meta';
import { useOfficialSpaceShare } from '../public/use-official-space-share';
import { getUserInfoByApi } from '../user/get-user';
import { canCopyToSpace, useCopyToSpace } from './use-copy-to-space';
import { useIsOwner } from './use-owner';
import { useReportIllegal } from './use-report-illegal';
import { usePersonalWikiId } from 'src/views/main/aside/wiki/use-personal-wiki-id';
import { useBlock } from '../block/use-block';
import { useCurrentSpace } from '../space/use-current-space';
import { request } from 'src/common/request';
import { cache } from 'src/redux/store';
import { WikiManager } from 'src/redux/managers/block/wiki-manager';
import { message } from '@flowus/common/components/message';
import { addSubscribe } from '@flowus/common';
import { findFirstAllowSubscribePage } from 'src/utils/block-utils';
import { removeWikiSuccessMessage } from 'src/views/main/aside/wiki/wiki-list';
import { setSubscribeStatus, useSubscribeStatus } from '../wiki/store';
import { useAppTranslation } from '@flowus/common/i18n/use-translation';

export const useGetShareHeaderMenuItems = (uuid: string) => {
  const { t } = useAppTranslation();
  const pageId = useGetPageId();
  const block = usePickBlock(uuid, ['data', 'createdBy'], ['segments']);
  const { allowDuplicate, shared, roleWithoutPublic, illegal } = usePermissions(uuid);
  const currentUser = useCurrentUser();
  const currentSpace = useCurrentSpace();
  const logout = useLogout();
  const isSharePage = judgeSharePage();
  const closeModal = useCloseModal();
  const report = useReportIllegal(uuid);
  const copyToSpace = useCopyToSpace();
  const isOwner = useIsOwner();
  const openModal = useOpenModal();
  const pageMeta = usePageMeta(pageId);
  const getQuery = useGetQuery(location.search);
  const getSharePageUrl = useGetSharePageUrl(uuid);
  const personalWikiId = usePersonalWikiId();
  const personalWikiBlock = useBlock(personalWikiId);
  const subscribed = useSubscribeStatus(pageId);
  const updateFromWiki = useCallback(
    (wikiId: string, pageId: string) => {
      WikiManager.removeReferencePage(wikiId, pageId);
      !isSharePage && message.success(t('common.unsubscribe_and_remove'));
    },
    [isSharePage, t]
  );

  const isSelected =
    subscribed ||
    !!personalWikiBlock?.subNodes.find((v) => {
      return cache.blocks[v]?.data.ref?.uuid === pageId;
    });

  const isBlockHasWiki = !!personalWikiBlock?.subNodes.find((v) => {
    return cache.blocks[v]?.data.ref?.uuid === pageId;
  });

  // 特殊空间
  const officialSpaceShare = useOfficialSpaceShare(pageId);
  const isHiddenReport =
    isOwner ||
    pageMeta?.tag === 'NO_PERMISSION' ||
    pageMeta?.tag === 'NOT_FOUND' ||
    illegal ||
    officialSpaceShare ||
    __PRIVATE__;
  const isNeedPay = useIsNeedPay(pageId);
  const isLogin = Boolean(currentUser.uuid);

  return useMemo(() => {
    if (!isSharePage || !block) return [];

    const isOwner = Boolean(roleWithoutPublic !== PermissionRole.NONE && isPageLike(block.type));

    const tempItems: ListItem[] = [
      {
        type: ListItemType.CUSTOM_RENDER,
        disableCustomHoverBg: true,
        render: () => {
          if (isLogin) {
            return (
              <div
                className="mx-2 my-1 flex h-10 cursor-pointer items-center rounded-lg px-2 animate-hover"
                onClick={() => {
                  closeModal(Modals.PAGE_HEADER_MENU_LIST);
                  locationToUrl(getUrlWithoutDomain());
                }}
              >
                <Avatar
                  className="mr-2 h-5 w-5 flex-shrink-0 rounded-full border border-black text-t4"
                  name={currentSpace.title}
                  color={currentSpace.backgroundColor}
                  icon={currentSpace.icon}
                  imgClassName="rounded-full"
                />
                <span className="text-t2 text-black">
                  {t('hooks.share_header_menu.back_to_my_space')}
                </span>
              </div>
            );
          }
          return (
            <div
              className="mx-2 flex h-10 cursor-pointer items-center px-2"
              onClick={async () => {
                const inviteCode = getQuery(SearchParams.inviteCode);
                const spaceId = getQuery(SearchParams.spaceId);
                closeModal(Modals.PAGE_HEADER_MENU_LIST);
                if (inviteCode && spaceId) {
                  locationToUrl(
                    splicePathSearchParams(ViewPath.login, [
                      {
                        key: SearchParams.callbackPath,
                        value: location.pathname,
                      },
                      {
                        key: SearchParams.inviteCode,
                        value: inviteCode,
                      },
                      {
                        key: SearchParams.spaceId,
                        value: spaceId,
                      },
                    ])
                  );
                  return;
                }
                if (block.createdBy) {
                  const user = await getUserInfoByApi(block.createdBy);
                  locationToUrl(
                    splicePathSearchParams(ViewPath.login, [
                      {
                        key: SearchParams.callbackPath,
                        value: location.pathname,
                      },
                      {
                        key: SearchParams.inviteCode,
                        value: `${user?.inviteCode}`,
                      },
                    ])
                  );
                  return;
                }
                locationToUrl(
                  splicePathSearchParams(ViewPath.login, [
                    {
                      key: SearchParams.callbackPath,
                      value: location.pathname,
                    },
                  ])
                );
              }}
            >
              <Icon size="middle" name="MIcSettingsAccount" className="mr-2"></Icon>
              <span className="text-t2 text-grey3">
                {t('hooks.share_header_menu.login_register')}
              </span>
            </div>
          );
        },
      },
    ];

    if (isLogin && isOwner) {
      tempItems.push(
        {
          type: ListItemType.LINE,
          disableClick: true,
        },
        {
          type: ListItemType.OPERATION,
          data: {
            icon: 'IcEdit',
            iconSize: 'middle',
            title: t('hooks.share_header_menu.enter_space_edit'),
            onClick: () => {
              locationToUrl(`${getUrlWithoutDomain()}/${pageId}`);
            },
          },
        }
      );
    }

    if (!illegal) {
      tempItems.push(
        {
          type: ListItemType.LINE,
          disableClick: true,
        },
        {
          type: ListItemType.OPERATION,
          data: {
            icon: 'IcLink',
            iconSize: 'middle',
            title: t('hooks.share_header_menu.copy_share_link'),
            onClick: () => {
              bizTracker.event('share_copylink', { is_share: shared });
              const { textAndUrl } = getSharePageUrl();
              void writeTextInClipboard(textAndUrl);
            },
          },
        },
        {
          type: ListItemType.OPERATION,
          data: {
            icon: 'IcGlobeShare',
            iconSize: 'middle',
            title: t('hooks.share_header_menu.copy_embed_link'),
            onClick: () => {
              bizTracker.event('share_copylink', { is_share: shared });
              const { textAndUrl } = getSharePageUrl({ embed: true });
              void writeTextInClipboard(textAndUrl);
            },
          },
        },
        {
          type: ListItemType.OPERATION,
          isHidden: !isLogin || !isSelected,
          data: {
            icon: 'IcKnowledgeBase',
            iconSize: 'middle',
            title: t('common.unsubscribe'),
            onClick: async () => {
              const lastSelected = isSelected;
              setSubscribeStatus(pageId, !lastSelected);
              // 兼容以前的订阅数据
              const targetId = findFirstAllowSubscribePage(pageId) ?? pageId;
              await addSubscribe(
                {
                  targetId,
                  subscribed: !lastSelected,
                  addSubscribe: request.infra.addSubscribe,
                  cancelSubscribe: request.infra.cancelSubscribe,
                  user: currentUser,
                  openDialog: openModal.warning,
                  callback: () => {},
                },
                {
                  pageId,
                  needPay: isNeedPay,
                  userId: currentUser.uuid,
                  createdBy: block?.createdBy,
                  spaceId: block?.spaceId,
                  event: bizTracker.event,
                }
              );

              if (lastSelected) {
                removeWikiSuccessMessage();
                message.success(t('common.unsubscribe_and_remove'));
              }
              if (isBlockHasWiki && personalWikiId) {
                updateFromWiki(personalWikiId, pageId);
              }
            },
          },
        }
      );

      if (canCopyToSpace(uuid) && !__PRIVATE__ && !isNeedPay) {
        tempItems.push({
          type: ListItemType.OPERATION,
          data: {
            icon: 'IcCopy',
            iconSize: 'middle',
            title: t('hooks.share_header_menu.copy_to_my_space'),
            onClick: () => {
              bizTracker.event('copytomyspace', {
                page_id: uuid,
              });
              void copyToSpace(uuid, { addCopyLimit: true });
            },
          },
        });
      }

      // 特殊空间不展示举报
      if (!isHiddenReport && !__PRIVATE__) {
        tempItems.push(
          {
            type: ListItemType.LINE,
            disableClick: true,
          },
          {
            type: ListItemType.OPERATION,
            data: {
              icon: 'IcReport',
              iconSize: 'middle',
              title: t('common.report_illegal'),
              onClick: report,
            },
          }
        );
      }
    }

    if (isLogin) {
      tempItems.push(
        {
          type: ListItemType.LINE,
          disableClick: true,
        },
        {
          type: ListItemType.OPERATION,
          data: {
            icon: 'IcLogOut',
            iconSize: 'middle',
            title: t('common.logout'),
            onClick: () => {
              bizTracker.logout();
              void logout();
            },
          },
        }
      );
    }

    if (!illegal && allowDuplicate) {
      tempItems.push(
        {
          type: ListItemType.LINE,
          disableClick: true,
        },
        {
          type: ListItemType.CUSTOM_RENDER,
          disableCustomHoverBg: true,
          render() {
            return (
              <div className="text-grey3 mx-4 my-2 flex items-center">
                <Icon name="IcShareDanger" size="small" className="mr-1" />
                <span className="text-t4">
                  {t('hooks.share_header_menu.allow_copy_download_print')}
                </span>
              </div>
            );
          },
        }
      );
    }

    return tempItems;
  }, [
    isSharePage,
    block,
    roleWithoutPublic,
    isLogin,
    illegal,
    allowDuplicate,
    t,
    closeModal,
    getQuery,
    pageId,
    isSelected,
    uuid,
    isNeedPay,
    isHiddenReport,
    shared,
    getSharePageUrl,
    currentUser,
    currentSpace,
    openModal.warning,
    isBlockHasWiki,
    personalWikiId,
    updateFromWiki,
    copyToSpace,
    report,
    logout,
  ]);
};
